import React from 'react'
import styled from 'styled-components'
import { useRevealProps } from '../../MwPage/components/Reveal'
import { compare as mwCompare, IDS } from '../../MwPage/content'
import { fillOffer } from '../../MwPage/offer'
import { CompareTable, PositioningMap } from '../../MwPage/sections/CompareSection'
import {
  cyan,
  dark,
  Eyebrow,
  Hi,
  ice2,
  iceLine,
  ink,
  Lede,
  line,
  muted,
  onDark,
  paper,
  sans,
  ScreenReader,
  Section,
  SectionHead,
  Small,
  Stack,
  ui,
} from '../../MwPage/styled'
import { useFocus } from '../focus'
import { H2, Wrap } from '../styled'

const Row = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(300px, 100%), 1fr));
  gap: 20px;
  align-items: stretch;

  /* the map keeps its square; the two figures stack to its height beside it */
  > figure {
    max-width: 440px;
  }
`

const Kpis = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  > * {
    flex: 1;
  }
`

// the prototype's .kpi: a figure and its note on a dark card
const Kpi = styled.div`
  padding: 20px;
  border-radius: 20px;
  background: ${dark};
  color: ${onDark};

  b.figure {
    display: block;
    margin-bottom: 6px;
    font: 800 28px/1.1 ${ui};
    letter-spacing: -0.04em;
    color: #2ec0d6;
  }

  p {
    margin: 0;
    font-size: 14.5px;
    line-height: 1.6;
  }

  p b {
    color: #ffffff;
  }
`

const TableTitle = styled.h3`
  margin: 0;
  font: 700 20px/1.3 ${sans};
  letter-spacing: -0.015em;
  color: ${ink};
`

// the prototype's .opt: one option's rows on a card, Jumpstart's edged in cyan
const Option = styled.div`
  padding: 14px 14px 8px;
  border-radius: 18px;
  border: 2px solid ${(props) => (props.$me ? cyan : iceLine)};
  background: ${(props) => (props.$me ? ice2 : paper)};
  ${(props) => props.$me && 'box-shadow: 0 8px 22px rgba(23, 195, 212, 0.15);'}

  h3 {
    margin: 0 0 6px;
    font: 800 16px/1.25 ${sans};
    letter-spacing: -0.01em;
    color: ${(props) => (props.$me ? '#0f7b8a' : ink)};
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  li {
    display: grid;
    grid-template-columns: 24px 1fr;
    column-gap: 9px;
    padding: 8px 0;
    border-top: 1px solid ${line};
  }

  li:first-child {
    border-top: 0;
  }

  small {
    grid-column: 2;
    font: 800 11.5px/1.3 ${ui};
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: ${muted};
  }

  span {
    grid-column: 2;
    font-size: 14.5px;
    line-height: 1.35;
    color: ${(props) => (props.$me ? ink : '#2b3339')};
    ${(props) => props.$me && 'font-weight: 700;'}
  }
`

const MARKS = {
  yes: { glyph: '✓', background: cyan, color: '#ffffff' },
  no: { glyph: '✕', background: '#eef1f3', color: '#8a949b' },
  mixed: { glyph: '–', background: '#fff4e5', color: '#9a5b00' },
}

const Mark = styled.i`
  grid-column: 1;
  grid-row: 1 / 3;
  display: grid;
  place-items: center;
  width: 22px;
  height: 22px;
  margin-top: 2px;
  border-radius: 50%;
  background: ${(props) => MARKS[props.$mark].background};
  color: ${(props) => MARKS[props.$mark].color};
  font: 800 11.5px/1 ${sans};
`

// the four option cards on one white panel, two by two from tablet width
const Panel = styled.div`
  padding: clamp(14px, 2.5vw, 24px);
  border-radius: 24px;
  background: ${paper};
  box-shadow: 0 18px 44px -20px rgba(10, 37, 64, 0.35), 0 2px 8px rgba(10, 37, 64, 0.06);
`

const OptionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;

  @media (min-width: 760px) {
    grid-template-columns: 1fr 1fr;
    gap: 18px;
  }
`

const RevealLede = ({ children }) => <Lede {...useRevealProps()}>{children}</Lede>

// the options side by side, each a card of marked rows
const Options = ({ compare, offer }) => (
  <>
    <Panel {...useRevealProps()}>
      <OptionGrid>
        {compare.options.map((option) => (
          <Option key={option.title} $me={option.me}>
            <h3>{option.title}</h3>
            <ul>
              {option.rows.map((row) => (
                <li key={row.label}>
                  <Mark aria-hidden="true" $mark={row.mark}>
                    {MARKS[row.mark].glyph}
                  </Mark>
                  <ScreenReader>{compare.marks[row.mark]}: </ScreenReader>
                  <small>{row.label}</small>
                  <span>{fillOffer(row.text, offer) || row.text.replace('{price}', '…')}</span>
                </li>
              ))}
            </ul>
          </Option>
        ))}
      </OptionGrid>
    </Panel>
    <Small>{compare.note}</Small>
  </>
)

// the MW page's positioning map, the two figures and the comparison table
const MapAndTable = ({ compare, offer }) => (
  <>
    <Row>
      <PositioningMap />
      <Kpis {...useRevealProps()}>
        {compare.kpis.map((kpi) => (
          <Kpi key={kpi.figure}>
            <b className="figure">{kpi.figure}</b>
            <p>
              <b>{kpi.strong}</b>
              {fillOffer(kpi.text, offer) || kpi.text.replace('{price}', '…')}
            </p>
          </Kpi>
        ))}
      </Kpis>
    </Row>
    <TableTitle {...useRevealProps()}>{compare.tableTitle}</TableTitle>
    <CompareTable offer={offer} table={compare.table} />
    <Small $maxWidth={84}>{mwCompare.note}</Small>
  </>
)

/** "What's different?": the page's options as cards, or the MW page's positioning map, figures and table. */
const CompareSection = ({ offer }) => {
  const { compare } = useFocus()

  return (
    <Section id={IDS.compare} $ice={!!compare.options}>
      <Wrap>
        <Stack $gap="clamp(28px, 3.5vw, 40px)">
          <SectionHead $maxWidth={880}>
            <Eyebrow {...useRevealProps()}>{compare.eyebrow}</Eyebrow>
            <H2 {...useRevealProps()}>
              {compare.headline} <Hi>{compare.headlineAccent}</Hi>
            </H2>
            {compare.options ? (
              <RevealLede>{fillOffer(compare.lede, offer) || compare.lede.replace('{price}', '…')}</RevealLede>
            ) : (
              <RevealLede>
                {compare.lede}
                <b style={{ color: ink }}>{compare.ledeStrong}</b>
                {compare.ledeAfter}
              </RevealLede>
            )}
          </SectionHead>
          {compare.options ? (
            <Options compare={compare} offer={offer} />
          ) : (
            <MapAndTable compare={compare} offer={offer} />
          )}
        </Stack>
      </Wrap>
    </Section>
  )
}

export default CompareSection
